import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, FileCheck, Users, Server, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const HipaaCompliancePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>HIPAA Compliance | CheckFirst - Healthcare Data Security</title>
        <meta
          name="description"
          content="Learn about CheckFirst's HIPAA compliance measures. We protect your health information with enterprise-grade security, encryption, and strict access controls."
        />
        <meta
          name="keywords"
          content="HIPAA compliance, healthcare data security, PHI protection, medical data privacy, healthcare cybersecurity, patient data protection"
        />
        <link rel="canonical" href="https://checkfirstcare.com/hipaa-compliance" />
        <meta property="og:title" content="HIPAA Compliance | CheckFirst" />
        <meta
          property="og:description"
          content="Enterprise-grade HIPAA compliance protecting your health information with encryption, access controls, and comprehensive security measures."
        />
        <meta property="og:url" content="https://checkfirstcare.com/hipaa-compliance" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://checkfirstcare.com/og-image.jpg" />
      </Helmet>

      <div className="container mx-auto px-4 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-primary hover:text-primary/80 transition-smooth mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            HIPAA Compliance
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your health information deserves the highest level of protection. 
            CheckFirst is fully committed to HIPAA compliance and data security.
          </p>
        </div>

        {/* Compliance Badge */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-6 py-3">
            <CheckCircle className="h-5 w-5 text-primary" />
            <span className="font-semibold text-foreground">HIPAA Compliant Platform</span>
          </div>
        </div>

        {/* Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Our Commitment to HIPAA</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-muted-foreground leading-relaxed">
              The Health Insurance Portability and Accountability Act (HIPAA) sets the standard for 
              protecting sensitive patient data. CheckFirst, founded by{" "}
              <Link to="/about" className="text-primary hover:underline font-medium">
                Ranvir Gill
              </Link>
              , implements comprehensive administrative, physical, and technical safeguards to ensure 
              the confidentiality, integrity, and availability of all Protected Health Information (PHI) 
              we process.
            </p>
          </CardContent>
        </Card>

        {/* Safeguards Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg">Administrative Safeguards</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-muted-foreground text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Designated Privacy and Security Officers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Regular workforce training on HIPAA requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Comprehensive policies and procedures</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Business Associate Agreements with all vendors</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Regular risk assessments and audits</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Lock className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg">Physical Safeguards</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-muted-foreground text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>SOC 2 Type II certified data centers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>24/7 physical security monitoring</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Controlled facility access with audit logs</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Secure workstation and device policies</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Proper media disposal procedures</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Server className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg">Technical Safeguards</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-muted-foreground text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>AES-256 encryption at rest and in transit</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Multi-factor authentication (MFA)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Role-based access controls (RBAC)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Comprehensive audit logging</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>Automatic session timeouts</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Your Rights */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FileCheck className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-2xl">Your HIPAA Rights</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Right to Access</h3>
                  <p className="text-muted-foreground text-sm">
                    Request copies of your Protected Health Information (PHI) in electronic or paper format.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Right to Amendment</h3>
                  <p className="text-muted-foreground text-sm">
                    Request corrections to any PHI you believe is inaccurate or incomplete.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Right to Accounting</h3>
                  <p className="text-muted-foreground text-sm">
                    Receive a list of disclosures we have made of your PHI for purposes other than treatment, payment, or healthcare operations.
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Right to Restrictions</h3>
                  <p className="text-muted-foreground text-sm">
                    Request restrictions on certain uses and disclosures of your PHI.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Right to Confidential Communications</h3>
                  <p className="text-muted-foreground text-sm">
                    Request that we communicate with you through alternative means or at alternative locations.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Right to Complain</h3>
                  <p className="text-muted-foreground text-sm">
                    File a complaint with us or the Department of Health and Human Services if you believe your privacy rights have been violated.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Security */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Eye className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-2xl">How We Protect Your Data</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-muted-foreground">
            <p>
              CheckFirst employs multiple layers of security to protect your health information:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Encryption</h4>
                <p className="text-sm">
                  All data is encrypted using AES-256 encryption both at rest and during transmission using TLS 1.3.
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Access Controls</h4>
                <p className="text-sm">
                  Strict role-based access ensures only authorized personnel can access your information.
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Audit Trails</h4>
                <p className="text-sm">
                  Every access to PHI is logged and monitored for unauthorized activity.
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Incident Response</h4>
                <p className="text-sm">
                  Comprehensive breach notification procedures ensure timely response to any security incidents.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-4">
                Questions About Our HIPAA Compliance?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Our Privacy Officer is available to answer any questions about how we protect your health information.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-smooth"
                >
                  Contact Us
                </Link>
                <Link
                  to="/privacy"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-border text-foreground font-medium hover:bg-muted transition-smooth"
                >
                  View Privacy Policy
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Updated */}
        <p className="text-center text-muted-foreground text-sm mt-8">
          Last updated: July 2025
        </p>
      </div>
    </div>
  );
};

export default HipaaCompliancePage;
